import React, { useEffect, useState, useCallback } from 'react';
import { 
  AnalyticalTable, 
  Button, 
  Dialog, 
  Title, 
  Select, 
  Option, 
  BusyIndicator, 
  Input, 
  Bar,
  Form,
  FormItem,
  FormGroup,
  MessageBox,
  MessageBoxType,
  MessageBoxAction,
  Text,
  Icon,
  Label 
} from '@ui5/webcomponents-react';
import axiosInstance from '../utils/axiosInstance';
import Layout from './Layout';
import usePermission from '../hooks/usePermission';
import useAutoTranslate from '../hooks/useAutoTranslate';

const UserList = () => {
  const { hasPermission, isSaasAdmin } = usePermission();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    sortBy: 'username',
    sortDirection: 'asc'
  });
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]); // Nuevo estado para las compañías
  const [suppliers, setSuppliers] = useState([]); // Nuevo estado para los proveedores
  const [tenants, setTenants] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [formData, setFormData] = useState({
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: {
      id: '',
      roleName: ''
    },
    companyType: '',
    enabled: true,
    position: '',
    tenantId: '',
    warehouseId: '',
    userCompanies: [], // Nuevo campo para las compañías del usuario
    userSuppliers: [] // Nuevo campo para los proveedores del usuario
  });
  const [formErrors, setFormErrors] = useState({});
  const { autoT } = useAutoTranslate();

  const fetchRoles = useCallback(async () => {
    try {
      const response = await axiosInstance.get('roles');
      if (response.data) {
        setRoles(response.data);
      }
    } catch (err) {
      console.error('Error al cargar los roles:', err);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const fetchTenants = useCallback(async () => {
    if (!isSaasAdmin()) return;
    try {
      const response = await axiosInstance.get('tenants?page=1&size=100');
      const records = response.data?.records?.data || response.data?.data || [];
      setTenants(Array.isArray(records) ? records : []);
    } catch (err) {
      console.error('Error al cargar tenants:', err);
    }
  }, [isSaasAdmin]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const fetchWarehousesByTenant = useCallback(async (tenantId) => {
    if (!tenantId) { setWarehouses([]); return; }
    try {
      const response = await axiosInstance.get(`warehouses/tenant/${tenantId}?size=100`);
      const records = response.data?.records?.data || response.data?.data || [];
      setWarehouses(Array.isArray(records) ? records : []);
    } catch (err) {
      console.error('Error al cargar bodegas:', err);
      setWarehouses([]);
    }
  }, []);

  const fetchCompanies = useCallback(async (type, tenantId) => {
    if (!type) {
      setCompanies([]);
      setSuppliers([]);
      return;
    }
    setLoading(true);
    try {
      let url = '';
      if (type === 'MANDANTE') {
        const currentTenantId = tenantId ||
          JSON.parse(localStorage.getItem('userData') || '{}').tenantId || '';
        url = `companies/tenant/${currentTenantId}`;
      } else if (type === 'PROVEEDOR') {
        url = 'suppliers';
      }
      
      if (url) {
        const response = await axiosInstance.get(url);
        if (response.data) {
          let fetchedData = response.data;
          if (response.data.records && Array.isArray(response.data.records.data)) {
            fetchedData = response.data.records.data;
          }
          
          if (type === 'MANDANTE') {
            setCompanies(Array.isArray(fetchedData) ? fetchedData : [fetchedData]);
            setSuppliers([]); // Clear suppliers when companyType is MANDANTE
          } else {
            setSuppliers(Array.isArray(fetchedData) ? fetchedData : []);
            setCompanies([]); // Clear companies when companyType is PROVEEDOR
          }
        }
      }
    } catch (err) {
      console.error('Error al cargar las compañías/proveedores:', err);
      setCompanies([]);
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (formData.companyType) {
      fetchCompanies(formData.companyType, formData.tenantId);
    }
  }, [formData.companyType, formData.tenantId, fetchCompanies]);

  const fetchUsers = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const url = `users?page=${page}&size=${pageSize}`;
      const response = await axiosInstance.get(url);

      if (response.data.records.code === "SUCCESS") {
        setData(response.data.records.data);
        setTotalPages(Math.ceil(response.data.totalCount / pageSize));
      } else {
        setError(response.data.records.message);
      }
    } catch (err) {
      setError('Error al cargar los usuarios: ' + (err.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  useEffect(() => {
    fetchUsers(currentPage);
  }, [currentPage, fetchUsers]);

  useEffect(() => {
    let filtered = [...data];

    // Filtrar por estatus
    if (selectedStatus !== 'all') {
      const isActive = selectedStatus === 'Activo';
      filtered = filtered.filter(item => item.enabled === isActive);
    }

    // Filtrar por término de búsqueda (nombre, username o email)
    if (searchTerm) {
      filtered = filtered.filter(item => 
        `${item.firstName} ${item.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Aplicar ordenamiento
    if (sortConfig.sortBy) {
      filtered.sort((a, b) => {
        let aValue, bValue;
        
        if (sortConfig.sortBy === 'fullName') {
          aValue = `${a.firstName} ${a.lastName}`.toLowerCase();
          bValue = `${b.firstName} ${b.lastName}`.toLowerCase();
        } else if (sortConfig.sortBy === 'enabled') {
          aValue = a.enabled ? 'Activo' : 'Inactivo';
          bValue = b.enabled ? 'Activo' : 'Inactivo';
        } else if (sortConfig.sortBy === 'role') {
          // Manejar el ordenamiento del nuevo objeto role
          aValue = a.role?.roleName?.toLowerCase() || '';
          bValue = b.role?.roleName?.toLowerCase() || '';
        } else {
          aValue = a[sortConfig.sortBy] ? a[sortConfig.sortBy].toString().toLowerCase() : '';
          bValue = b[sortConfig.sortBy] ? b[sortConfig.sortBy].toString().toLowerCase() : '';
        }
        
        if (aValue < bValue) {
          return sortConfig.sortDirection === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.sortDirection === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredData(filtered);
  }, [selectedStatus, searchTerm, data, sortConfig]);

  const handleFilterStatusChange = (event) => {
    setSelectedStatus(event.detail.selectedOption.value);
  };

  const handleSort = (event) => {
    const column = event.detail.column;
    const sortDirection = event.detail.sortDirection;
  
    setSortConfig({
      sortBy: column.accessor,
      sortDirection: sortDirection
    });
  };

  const handleRowClick = (event) => {
    const { detail } = event;
    if (detail && detail.row && detail.row.original) {
      // Solo permitir editar si tiene el permiso de actualización
      if (!hasPermission('users', 'update')) {
        return;
      }
      
      const user = detail.row.original;
      setSelectedUser(user);
      
      // Convertir el companyType si es necesario
      let companyTypeValue = user.companyType;
      if (companyTypeValue === 'MANDANTE') {
        companyTypeValue = 'MANDANTE';
      } else if (companyTypeValue === 'PROVEEDOR') {
        companyTypeValue = 'PROVEEDOR';
      }
      
      // Inicializar el formulario con los datos del usuario seleccionado
      const editTenantId = user.tenantId || '';
      setFormData({
        username: user.username || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        password: '', // No mostrar la contraseña por seguridad
        role: {
          id: user.role?.id || '',
          roleName: user.role?.roleName || ''
        },
        companyType: companyTypeValue,
        enabled: user.enabled !== undefined ? user.enabled : true,
        position: user.position || '',
        tenantId: editTenantId,
        warehouseId: user.warehouseId || '',
        userCompanies: user.userCompanies || [], // Inicializar userCompanies
        userSuppliers: user.userSuppliers || [] // Inicializar userSuppliers
      });
      // Cargar bodegas del tenant del usuario para el selector de edición
      if (editTenantId) {
        fetchWarehousesByTenant(editTenantId);
      } else {
        setWarehouses([]);
      }
      
      // Mostrar el diálogo de edición
      setShowEditDialog(true);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prevPage => prevPage + 1);
    }
  };

  const handlePreviousPage = () => {
    setCurrentPage(prevPage => Math.max(prevPage - 1, 1));
  };

  const handleCreateUser = () => {
    // Solo permitir crear usuarios si tiene el permiso de creación
    if (!hasPermission('users', 'create')) {
      return;
    }
    
    setSelectedUser(null);
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const defaultTenantId = isSaasAdmin() ? '' : (userData.tenantId || '');
    setFormData({
      username: '',
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      role: {
        id: '',
        roleName: ''
      },
      companyType: '', // Inicializar a cadena vacía
      enabled: true,
      position: '',
      tenantId: defaultTenantId,
      warehouseId: '',
      userCompanies: [], // Inicializar userCompanies para la creación
      userSuppliers: [] // Inicializar userSuppliers para la creación
    });
    setWarehouses([]);
    // Si no es SaaS admin, cargar bodegas de su tenant inmediatamente
    if (!isSaasAdmin() && defaultTenantId) {
      fetchWarehousesByTenant(defaultTenantId);
    }
    setFormErrors({});
    setShowCreateDialog(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar el error de este campo si existe
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleSelectChange = (e) => {
    const name = e.target.getAttribute('name');
    const value = e.detail.selectedOption.value;
    
    if (name === 'role') {
      const selectedRole = roles.find(role => role.id === value);
      if (selectedRole) {
        setFormData(prev => ({
          ...prev,
          role: {
            id: selectedRole.id,
            roleName: selectedRole.roleName
          }
        }));
      }
    } else if (name === 'companyType') {
      setFormData(prev => ({
        ...prev,
        companyType: value, // Directly use the selected value
        userCompanies: [], // Reset userCompanies when companyType changes
        userSuppliers: [] // Reset userSuppliers when companyType changes
      }));
      
      // Limpiar el error si existía
      if (formErrors.companyType) {
        setFormErrors(prev => ({
          ...prev,
          companyType: null
        }));
      }
    } else if (name === 'company') {
      const selectedCompany = companies.find(comp => (comp.id || comp.idSupplier) === value);
      if (selectedCompany) {
        setFormData(prev => ({
          ...prev,
          userCompanies: [{
            company: {
              id: selectedCompany.id || selectedCompany.idSupplier,
              name: selectedCompany.name || selectedCompany.companyName || selectedCompany.businessName
            }
          }],
          userSuppliers: [] // Clear userSuppliers if a company is selected
        }));
      }
      if (formErrors.userCompanies) {
        setFormErrors(prev => ({
          ...prev,
          userCompanies: null
        }));
      }
    } else if (name === 'supplier') {
      const selectedSupplier = suppliers.find(sup => (sup.id || sup.idSupplier) === value);
      if (selectedSupplier) {
        setFormData(prev => ({
          ...prev,
          userSuppliers: [{
            supplier: {
              id: selectedSupplier.id || selectedSupplier.idSupplier,
              supplierName: selectedSupplier.supplierName || selectedSupplier.name || selectedSupplier.businessName
            }
          }],
          userCompanies: [] // Clear userCompanies if a supplier is selected
        }));
      }
      if (formErrors.userSuppliers) {
        setFormErrors(prev => ({
          ...prev,
          userSuppliers: null
        }));
      }
    } else if (name === 'tenantId') {
      setFormData(prev => ({ ...prev, tenantId: value, warehouseId: '' }));
      fetchWarehousesByTenant(value);
    } else if (name === 'warehouseId') {
      setFormData(prev => ({ ...prev, warehouseId: value }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleToggleEnabled = () => {
    setFormData(prev => ({
      ...prev,
      enabled: !prev.enabled
    }));
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.username.trim()) errors.username = 'El nombre de usuario es obligatorio';
    if (!formData.firstName.trim()) errors.firstName = 'El nombre es obligatorio';
    if (!formData.lastName.trim()) errors.lastName = 'El apellido es obligatorio';
    if (!formData.email.trim()) errors.email = 'El email es obligatorio';
    if (!formData.email.includes('@')) errors.email = 'El email debe ser válido';
    
    // Validar companyType
    if (!formData.companyType || (formData.companyType !== 'MANDANTE' && formData.companyType !== 'PROVEEDOR')) {
      errors.companyType = 'El tipo de compañía es obligatorio';
    }
    
    // Validar userCompanies si se ha seleccionado MANDANTE
    if (formData.companyType === 'MANDANTE' && (!formData.userCompanies || formData.userCompanies.length === 0 || !formData.userCompanies[0].company?.id)) {
      errors.userCompanies = 'La compañía es obligatoria';
    }
    
    // Validar userSuppliers si se ha seleccionado PROVEEDOR
    if (formData.companyType === 'PROVEEDOR' && (!formData.userSuppliers || formData.userSuppliers.length === 0 || !(formData.userSuppliers[0].supplier?.id || formData.userSuppliers[0].supplier?.idSupplier))) {
      errors.userSuppliers = 'El proveedor es obligatorio';
    }
    
    // Solo validar contraseña en creación, no en edición
    if (!selectedUser && !formData.password.trim()) {
      errors.password = 'La contraseña es obligatoria';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitCreate = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      if (!formData.role.id) {
        setFormErrors({
          general: 'Por favor seleccione un rol válido'
        });
        setLoading(false);
        return;
      }

      const dataToSend = { ...formData };

      dataToSend.roleId = formData.role.id;
      delete dataToSend.role;

      dataToSend.companyType = formData.companyType || 'MANDANTE';
      dataToSend.tenantId = formData.tenantId || null;
      dataToSend.warehouseId = formData.warehouseId || null;

      if (dataToSend.position === undefined) {
        dataToSend.position = '';
      }

      if (formData.userCompanies && formData.userCompanies.length > 0) {
        dataToSend.userCompanies = formData.userCompanies;
      }

      if (formData.userSuppliers && formData.userSuppliers.length > 0) {
        dataToSend.userSuppliers = formData.userSuppliers;
      }

      const response = await axiosInstance.post('users', dataToSend);
      
      if (response.data.code === "SUCCESS") {
        setShowCreateDialog(false);
        fetchUsers(currentPage);
      } else {
        setFormErrors({
          general: response.data.message || 'Error al crear el usuario'
        });
      }
    } catch (err) {
      console.error('Error al crear usuario:', err);
      setFormErrors({
        general: err.response?.data?.message || 'Error al crear el usuario'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitEdit = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      // Verificar que tengamos un rol válido
      if (!formData.role.id) {
        setFormErrors({
          general: 'Por favor seleccione un rol válido'
        });
        setLoading(false);
        return;
      }

      // Crear una copia del objeto para no modificar el estado directamente
      const dataToSend = { ...formData };
      
      // Extraer el roleId y eliminar el objeto role para evitar conflictos
      dataToSend.roleId = formData.role.id;
      delete dataToSend.role;
      
      // Asegurar que companyType sea el valor numérico correcto
      dataToSend.companyType = formData.companyType || 'MANDANTE';
      
      if (!dataToSend.password) {
        delete dataToSend.password;
      }
      
      if (dataToSend.position === undefined) {
        dataToSend.position = '';
      }
      
      // Incluir userCompanies si existe
      if (formData.userCompanies && formData.userCompanies.length > 0) {
        dataToSend.userCompanies = formData.userCompanies;
      }
      
      if (formData.userSuppliers && formData.userSuppliers.length > 0) {
        dataToSend.userSuppliers = formData.userSuppliers;
      }
      
      const userId = selectedUser.id;
      const response = await axiosInstance.put(`users/${userId}`, dataToSend);
      
      if (response.data.code === "SUCCESS") {
        setShowEditDialog(false);
        fetchUsers(currentPage);
      } else {
        setFormErrors({
          general: response.data.message || 'Error al actualizar el usuario'
        });
      }
    } catch (err) {
      console.error('Error al actualizar usuario:', err);
      setFormErrors({
        general: err.response?.data?.message || 'Error al actualizar el usuario'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    // Solo permitir eliminar usuarios si tiene el permiso de eliminación
    if (!hasPermission('users', 'delete')) {
      return;
    }
    
    if (selectedUser) {
      setShowDeleteConfirm(true);
    }
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    try {
      // Usar el ID del usuario en lugar del username
      const userId = selectedUser.id || selectedUser.username;
      const response = await axiosInstance.delete(`users/${userId}`);
      
      if (response.data.code === "SUCCESS") {
        setShowDeleteConfirm(false);
        setShowEditDialog(false);
        fetchUsers(currentPage);
      } else {
        setFormErrors({
          general: response.data.message || 'Error al eliminar el usuario'
        });
      }
    } catch (err) {
      setFormErrors({
        general: err.response?.data?.message || 'Error al eliminar el usuario'
      });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { 
      Header: 'Username', 
      accessor: 'username', 
      sortable: true, 
      hAlign: 'center',
      width: 150
    },
    { 
      Header: 'Nombre Completo', 
      accessor: 'fullName', 
      sortable: true, 
      hAlign: 'center',
      width: 200,
      Cell: ({ row }) => `${row.original.firstName} ${row.original.lastName}`
    },
    { 
      Header: 'Email', 
      accessor: 'email', 
      sortable: true, 
      hAlign: 'center',
      width: 220
    },
    { 
      Header: 'Rol', 
      accessor: 'role', 
      sortable: true, 
      hAlign: 'center',
      width: 120,
      Cell: ({ value }) => {
        const roles = {
          'ADMIN': 'Administrador',
          'USER': 'Usuario',
          'SUPERVISOR': 'Supervisor'
        };
        const roleName = value?.roleName || value;
        return roles[roleName] || roleName;
      }
    },
    { 
      Header: 'Compañía', 
      accessor: 'companyName', // Usar un accessor diferente para el ordenamiento
      sortable: true,
      hAlign: 'center',
      width: 150,
      Cell: ({ row }) => {
        const user = row.original;
        if (user.companyType === 'MANDANTE' && user.userCompanies?.length > 0) {
          return user.userCompanies[0].company?.name || '';
        } else if (user.companyType === 'PROVEEDOR' && user.userSuppliers?.length > 0) {
          return user.userSuppliers[0].supplier?.supplierName || user.userSuppliers[0].supplier?.name || '';
        }
        return '';
      }
    },
    { 
      Header: 'Tipo de compañía', 
      accessor: 'companyType', 
      sortable: true, 
      hAlign: 'center',
      width: 150
    },
    { 
      Header: 'Posición', 
      accessor: 'position', 
      sortable: true, 
      hAlign: 'center',
      width: 200
    },
    { 
      Header: 'Estatus', 
      accessor: 'enabled', 
      sortable: true, 
      hAlign: 'center',
      width: 100,
      Cell: ({ value }) => (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          color: value ? '#28a745' : '#dc3545'
        }}>
          <Icon name={value ? "status-positive" : "status-negative"} />
          <span style={{ marginLeft: '5px' }}>{value ? 'Activo' : 'Inactivo'}</span>
        </div>
      )
    }
  ];

  if (loading && data.length === 0) return (
    <Layout>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: 'calc(100vh - 100px)' 
      }}>
        <BusyIndicator active size="Large" />
      </div>
    </Layout>
  );

  if (error && data.length === 0) return (
    <Layout>
      <div style={{ padding: '20px' }}>
        <Title level="H4">{autoT('Error')}</Title>
        <Text>{error}</Text>
        <Button onClick={() => fetchUsers(1)} style={{ marginTop: '10px' }}>{autoT('Reintentar')}</Button>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div style={{ padding: '0 20px' }}>
        <Bar
          design="Header"
          startContent={
            <Title level="H4">{autoT('Listado de usuarios')}</Title>
          }
          endContent={
            hasPermission('users', 'create') && (
              <Button 
                design="Emphasized" 
                onClick={handleCreateUser}
                icon="add"
              >
                {autoT('Crear Usuario')}
              </Button>
            )
          }
          style={{ marginBottom: '20px', padding: '0 10px' }}
        />

        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'center' }}>
          <Select
            onChange={handleFilterStatusChange}
            value={selectedStatus}
          >
            <Option value="all">{autoT('Todos los estados')}</Option>
            <Option value="Activo">{autoT('Activo')}</Option>
            <Option value="Inactivo">{autoT('Inactivo')}</Option>
          </Select>
          <Input
            placeholder={autoT('Buscar por nombre, usuario o email')}
            value={searchTerm}
            onInput={(e) => setSearchTerm(e.target.value)}
            style={{ width: '300px' }}
            icon="search"
          />
        </div>
        
        <div style={{ marginBottom: '20px' }}>
          <AnalyticalTable
            data={filteredData}
            columns={columns}
            minRows={10}
            visibleRows={10}
            alternateRowColor
            header
            onRowClick={hasPermission('users', 'update') ? handleRowClick : undefined}
            onSort={handleSort}
            sortable
            style={{
              width: '100%',
              '--sapList_HeaderBackground': '#f5f6f7',
              '--sapList_BorderColor': '#e5e5e5',
            }}
            noDataText={autoT('No se encontraron usuarios')}
          />
        </div>

        <div style={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px 0'
        }}>
          <Button 
            onClick={handlePreviousPage} 
            disabled={currentPage === 1}
            icon="nav-back"
          >
            Anterior
          </Button>
          <Text>Página {currentPage} de {totalPages}</Text>
          <Button 
            onClick={handleNextPage} 
            disabled={currentPage >= totalPages}
            icon="nav-forward"
          >
            Siguiente
          </Button>
        </div>
      </div>

      {/* Dialog para crear usuario */}
      <Dialog
        open={showCreateDialog}
        header={
          <Bar
            startContent={
              <Title level="H4">{autoT('Crear Nuevo Usuario')}</Title>
            }
          />
        }
        onAfterClose={() => setShowCreateDialog(false)}
        style={{ width: '800px' }}
        footer={
          <Bar
            endContent={
              <>
                <Button design="Transparent" onClick={() => setShowCreateDialog(false)}>{autoT('Cancelar')}</Button>
                <Button design="Emphasized" onClick={handleSubmitCreate}>{autoT('Crear')}</Button>
              </>
            }
          />
        }
      >
        <Form 
          style={{ padding: '1rem' }}
          titleText="Información del Usuario"
          backgroundDesign="Transparent"
          columnsXL={2}
          columnsL={2}
          columnsM={2}
          columnsS={1}
          labelSpanXL={4}
          labelSpanL={4}
          labelSpanM={12}
          labelSpanS={12}
        >
          {formErrors.general && (
            <FormGroup>
              <FormItem>
                <Text style={{ color: 'var(--sapErrorColor)', marginBottom: '1rem' }}>{formErrors.general}</Text>
              </FormItem>
            </FormGroup>
          )}
          
          <FormGroup titleText={autoT('Información de cuenta')}>
            <FormItem labelContent={<Label>{autoT('Usuario')}</Label>}>
              <Input 
                name="username" 
                value={formData.username} 
                onChange={handleInputChange}
                valueState={formErrors.username ? "Error" : "None"}
                valueStateMessage={formErrors.username}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Contraseña')}</Label>}>
              <Input 
                name="password" 
                type="Password"
                value={formData.password} 
                onChange={handleInputChange}
                valueState={formErrors.password ? "Error" : "None"}
                valueStateMessage={formErrors.password}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Email')}</Label>}>
              <Input 
                name="email" 
                type="Email"
                value={formData.email} 
                onChange={handleInputChange}
                valueState={formErrors.email ? "Error" : "None"}
                valueStateMessage={formErrors.email}
                required
              />
            </FormItem>
          </FormGroup>
          
          <FormGroup titleText={autoT('Información Personal')}>
            <FormItem labelContent={<Label>{autoT('Nombre')}</Label>}>
              <Input 
                name="firstName" 
                value={formData.firstName} 
                onChange={handleInputChange}
                valueState={formErrors.firstName ? "Error" : "None"}
                valueStateMessage={formErrors.firstName}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Apellido')}</Label>}>
              <Input 
                name="lastName" 
                value={formData.lastName} 
                onChange={handleInputChange}
                valueState={formErrors.lastName ? "Error" : "None"}
                valueStateMessage={formErrors.lastName}
                required
              />
            </FormItem>
          </FormGroup>
          
          <FormGroup titleText={autoT('Configuración')}>
            <FormItem labelContent={<Label>{autoT('Rol')}</Label>}>
              <Select 
                name="role"
                onChange={handleSelectChange}
                value={formData.role.id}
                required
              >
                <Option value="">{autoT('Seleccione un rol')}</Option>
                {roles.map(role => (
                  <Option 
                    key={role.id} 
                    value={role.id}
                    selected={role.id === formData.role.id}
                  >
                    {role.roleName}
                  </Option>
                ))}
              </Select>
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Tipo de compañia')}</Label>}>
              <Select 
                name="companyType"
                onChange={handleSelectChange}
                value={formData.companyType}
                valueState={formErrors.companyType ? "Error" : "None"}
                valueStateMessage={formErrors.companyType}
                required
              >
                <Option value="">{autoT('Seleccione tipo de compañía')}</Option>
                <Option value="MANDANTE" selected={"MANDANTE" === formData.companyType}>{autoT('Mandante')}</Option>
                <Option value="PROVEEDOR" selected={"PROVEEDOR" === formData.companyType}>{autoT('Proveedor')}</Option>
              </Select>
            </FormItem>
            {(formData.companyType === 'MANDANTE') && (
              <FormItem labelContent={<Label>{autoT('Compañía')}</Label>}>
                <Select 
                  name="company"
                  onChange={handleSelectChange}
                  value={formData.userCompanies.length > 0 ? (formData.userCompanies[0].company?.id || '') : ''}
                  valueState={formErrors.userCompanies ? "Error" : "None"}
                  valueStateMessage={formErrors.userCompanies}
                  required
                >
                  <Option value="">{autoT('Seleccione una compañía')}</Option>
                  {companies.map(company => (
                    <Option 
                        key={company.id || company.idSupplier} 
                        value={company.id || company.idSupplier}
                        selected={(company.id || company.idSupplier) === (formData.userCompanies.length > 0 ? formData.userCompanies[0].company?.id || '' : '')}
                    >
                      {company.companyName || company.businessName || company.name}
                    </Option>
                  ))}
                </Select>
              </FormItem>
            )}
            {(formData.companyType === 'PROVEEDOR') && (
              <FormItem labelContent={<Label>{autoT('Proveedor')}</Label>}>
                <Select 
                  name="supplier"
                  onChange={handleSelectChange}
                  value={formData.userSuppliers.length > 0 ? (formData.userSuppliers[0].supplier?.id || formData.userSuppliers[0].supplier?.idSupplier || '') : ''}
                  valueState={formErrors.userSuppliers ? "Error" : "None"}
                  valueStateMessage={formErrors.userSuppliers}
                  required
                >
                  <Option value="">{autoT('Seleccione un proveedor')}</Option>
                  {suppliers.map(supplier => (
                    <Option 
                        key={supplier.id || supplier.idSupplier} 
                        value={supplier.id || supplier.idSupplier}
                        selected={(supplier.id || supplier.idSupplier) === (formData.userSuppliers.length > 0 ? formData.userSuppliers[0].supplier?.id || formData.userSuppliers[0].supplier?.idSupplier || '' : '')}
                    >
                      {supplier.supplierName || supplier.name || supplier.businessName}
                    </Option>
                  ))}
                </Select>
              </FormItem>
            )}
            {/* Selector de Tenant — solo visible para SaaS Admin */}
            {isSaasAdmin() && (
              <FormItem labelContent={<Label>{autoT('Tenant')}</Label>}>
                <Select
                  name="tenantId"
                  onChange={handleSelectChange}
                  value={formData.tenantId}
                >
                  <Option value="">{autoT('-- Seleccionar Tenant --')}</Option>
                  {tenants.map(t => (
                    <Option key={t.id} value={t.id} selected={t.id === formData.tenantId}>
                      {t.name}
                    </Option>
                  ))}
                </Select>
              </FormItem>
            )}
            {/* Selector de Bodega — visible cuando hay bodegas disponibles */}
            {warehouses.length > 0 && (
              <FormItem labelContent={<Label>{autoT('Bodega')}</Label>}>
                <Select
                  name="warehouseId"
                  onChange={handleSelectChange}
                  value={formData.warehouseId}
                >
                  <Option value="">{autoT('-- Sin bodega --')}</Option>
                  {warehouses.map(w => (
                    <Option key={w.id} value={w.id} selected={w.id === formData.warehouseId}>
                      {w.name}
                    </Option>
                  ))}
                </Select>
              </FormItem>
            )}
            <FormItem labelContent={<Label>{autoT('Posición')}</Label>}>
              <Select
                name="position"
                onChange={handleSelectChange}
                value={formData.position}
              >
                <Option value="">{autoT('Seleccione una posición')}</Option>
                <Option value="Recepcion Zona 1 - Centro" selected={"Recepcion Zona 1 - Centro" === formData.position}>{autoT('Recepcion Zona 1 - Centro')}</Option>
                <Option value="Recepcion Zona 2 - Centro" selected={"Recepcion Zona 2 - Centro" === formData.position}>{autoT('Recepcion Zona 2 - Centro')}</Option>
                <Option value="Recepcion Zona 1 - Bodega Repuestos" selected={"Recepcion Zona 1 - Bodega Repuestos" === formData.position}>{autoT('Recepcion Zona 1 - Bodega Repuestos')}</Option>
              </Select>
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Estado')}</Label>}>
              <Button
                icon={formData.enabled ? "status-positive" : "status-negative"}
                design="Transparent"
                onClick={handleToggleEnabled}
              >
                {formData.enabled ? autoT('Activo') : autoT('Inactivo')}
              </Button>
            </FormItem>
          </FormGroup>
        </Form>
      </Dialog>

      {/* Dialog para editar usuario */}
      <Dialog
        open={showEditDialog}
        header={
          <Bar
            startContent={
              <Title level="H4">{autoT('Editar Usuario')}: {selectedUser?.username}</Title>
            }
          />
        }
        onAfterClose={() => setShowEditDialog(false)}
        style={{ width: '800px' }}
        footer={
          <Bar
            startContent={
              hasPermission('users', 'delete') && (
                <Button design="Negative" onClick={handleDeleteClick} icon="delete">{autoT('Eliminar')}</Button>
              )
            }
            endContent={
              <>
                <Button design="Transparent" onClick={() => setShowEditDialog(false)}>{autoT('Cancelar')}</Button>
                <Button design="Emphasized" onClick={handleSubmitEdit}>{autoT('Guardar')}</Button>
              </>
            }
          />
        }
      >
        <Form 
          style={{ padding: '1rem' }}
          titleText="Información del Usuario"
          backgroundDesign="Transparent"
          columnsXL={2}
          columnsL={2}
          columnsM={2}
          columnsS={1}
          labelSpanXL={4}
          labelSpanL={4}
          labelSpanM={12}
          labelSpanS={12}
        >
          {formErrors.general && (
            <FormGroup>
              <FormItem>
                <Text style={{ color: 'var(--sapErrorColor)', marginBottom: '1rem' }}>{formErrors.general}</Text>
              </FormItem>
            </FormGroup>
          )}
          
          <FormGroup titleText={autoT('Información de cuenta')}>
            <FormItem labelContent={<Label>{autoT('Usuario')}</Label>}>
              <Input 
                name="username" 
                value={formData.username} 
                onChange={handleInputChange}
                valueState={formErrors.username ? "Error" : "None"}
                valueStateMessage={formErrors.username}
                disabled
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Contraseña (Dejar en blanco para no cambiar)')}</Label>}>
              <Input 
                name="password" 
                type="Password"
                value={formData.password} 
                onChange={handleInputChange}
                valueState={formErrors.password ? "Error" : "None"}
                valueStateMessage={formErrors.password}
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Email')}</Label>}>
              <Input 
                name="email" 
                type="Email"
                value={formData.email} 
                onChange={handleInputChange}
                valueState={formErrors.email ? "Error" : "None"}
                valueStateMessage={formErrors.email}
                required
              />
            </FormItem>
          </FormGroup>
          
          <FormGroup titleText={autoT('Información Personal')}>
            <FormItem labelContent={<Label>{autoT('Nombre')}</Label>}>
              <Input 
                name="firstName" 
                value={formData.firstName} 
                onChange={handleInputChange}
                valueState={formErrors.firstName ? "Error" : "None"}
                valueStateMessage={formErrors.firstName}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Apellido')}</Label>}>
              <Input 
                name="lastName" 
                value={formData.lastName} 
                onChange={handleInputChange}
                valueState={formErrors.lastName ? "Error" : "None"}
                valueStateMessage={formErrors.lastName}
                required
              />
            </FormItem>
          </FormGroup>
          
          <FormGroup titleText={autoT('Configuración')}>
            <FormItem labelContent={<Label>{autoT('Rol')}</Label>}>
              <Select 
                name="role"
                onChange={handleSelectChange}
                value={formData.role.id}
                required
              >
                <Option value="">{autoT('Seleccione un rol')}</Option>
                {roles.map(role => (
                  <Option 
                    key={role.id} 
                    value={role.id}
                    selected={role.id === formData.role.id}
                  >
                    {role.roleName}
                  </Option>
                ))}
              </Select>
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Tipo Compañia')}</Label>}>
              <Select 
                name="companyType"
                onChange={handleSelectChange}
                value={formData.companyType}
                valueState={formErrors.companyType ? "Error" : "None"}
                valueStateMessage={formErrors.companyType}
                required
              >
                <Option value="">{autoT('Seleccione tipo de compañía')}</Option>
                <Option value="MANDANTE" selected={"MANDANTE" === formData.companyType}>{autoT('Mandante')}</Option>
                <Option value="PROVEEDOR" selected={"PROVEEDOR" === formData.companyType}>{autoT('Proveedor')}</Option>
              </Select>
            </FormItem>
            {(formData.companyType === 'MANDANTE') && (
              <FormItem labelContent={<Label>{autoT('Compañía')}</Label>}>
                <Select 
                  name="company"
                  onChange={handleSelectChange}
                  value={formData.userCompanies.length > 0 ? (formData.userCompanies[0].company?.id || '') : ''}
                  valueState={formErrors.userCompanies ? "Error" : "None"}
                  valueStateMessage={formErrors.userCompanies}
                  required
                >
                  <Option value="" selected={formData.userCompanies.length === 0 || !formData.userCompanies[0].company?.id}>{autoT('Seleccione una compañía')}</Option>
                  {companies.map(company => (
                    <Option 
                        key={company.id || company.idSupplier} 
                        value={company.id || company.idSupplier}
                        selected={(company.id || company.idSupplier) === (formData.userCompanies.length > 0 ? formData.userCompanies[0].company?.id || '' : '')}
                    >
                      {company.companyName || company.businessName || company.name}
                    </Option>
                  ))}
                </Select>
              </FormItem>
            )}
            {(formData.companyType === 'PROVEEDOR') && (
              <FormItem labelContent={<Label>{autoT('Proveedor')}</Label>}>
                <Select 
                  name="supplier"
                  onChange={handleSelectChange}
                  value={formData.userSuppliers.length > 0 ? (formData.userSuppliers[0].supplier?.id || formData.userSuppliers[0].supplier?.idSupplier || '') : ''}
                  valueState={formErrors.userSuppliers ? "Error" : "None"}
                  valueStateMessage={formErrors.userSuppliers}
                  required
                >
                  <Option value="" selected={formData.userSuppliers.length === 0 || !formData.userSuppliers[0].supplier?.id}>{autoT('Seleccione un proveedor')}</Option>
                  {suppliers.map(supplier => (
                    <Option 
                        key={supplier.id || supplier.idSupplier} 
                        value={supplier.id || supplier.idSupplier}
                        selected={(supplier.id || supplier.idSupplier) === (formData.userSuppliers.length > 0 ? formData.userSuppliers[0].supplier?.id || formData.userSuppliers[0].supplier?.idSupplier || '' : '')}
                    >
                      {supplier.supplierName || supplier.name || supplier.businessName}
                    </Option>
                  ))}
                </Select>
              </FormItem>
            )}
            {/* Selector de Bodega en edición — visible cuando hay bodegas disponibles */}
            {warehouses.length > 0 && (
              <FormItem labelContent={<Label>{autoT('Bodega')}</Label>}>
                <Select
                  name="warehouseId"
                  onChange={handleSelectChange}
                  value={formData.warehouseId}
                >
                  <Option value="">{autoT('-- Sin bodega --')}</Option>
                  {warehouses.map(w => (
                    <Option key={w.id} value={w.id} selected={w.id === formData.warehouseId}>
                      {w.name}
                    </Option>
                  ))}
                </Select>
              </FormItem>
            )}
            <FormItem labelContent={<Label>{autoT('Posición')}</Label>}>
              <Select
                name="position"
                onChange={handleSelectChange}
                value={formData.position}
              >
                <Option value="">{autoT('Seleccione una posición')}</Option>
                <Option value="Recepcion Zona 1 - Centro" selected={"Recepcion Zona 1 - Centro" === formData.position}>{autoT('Recepcion Zona 1 - Centro')}</Option>
                <Option value="Recepcion Zona 2 - Centro" selected={"Recepcion Zona 2 - Centro" === formData.position}>{autoT('Recepcion Zona 2 - Centro')}</Option>
                <Option value="Recepcion Zona 1 - Bodega Repuestos" selected={"Recepcion Zona 1 - Bodega Repuestos" === formData.position}>{autoT('Recepcion Zona 1 - Bodega Repuestos')}</Option>
              </Select>
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Estado')}</Label>}>
              <Button
                icon={formData.enabled ? "status-positive" : "status-negative"}
                design="Transparent"
                onClick={handleToggleEnabled}
              >
                {formData.enabled ? autoT('Activo') : autoT('Inactivo')}
              </Button>
            </FormItem>
          </FormGroup>
        </Form>
      </Dialog>
      {/* Diálogo de confirmación para eliminar */}
      <MessageBox
        open={showDeleteConfirm}
        onClose={(event) => {
          if (event.detail.action === MessageBoxAction.OK) {
            handleConfirmDelete();
          } else {
            setShowDeleteConfirm(false);
          }
        }}
        title={autoT('Confirmar eliminación')}
        type={MessageBoxType.WARNING}
        actions={[MessageBoxAction.OK, MessageBoxAction.CANCEL]}
      >
        {autoT('¿Está seguro que desea eliminar el usuario')} <strong>{selectedUser?.username}</strong>? {autoT('Esta acción no se puede deshacer.')}
      </MessageBox>
    </Layout>
  );
};

export default UserList;